/**
 * Nord Living domain permissions (studio work, finance, integrations, portal).
 * Composed into the registry by `permission-registry.ts`.
 */

import type {
  PermissionCategory,
  PermissionDefinition,
} from "./permission-registry.types";

type Row = readonly [code: string, name: string, description: string];

function group(
  category: PermissionCategory,
  rows: readonly Row[],
): PermissionDefinition[] {
  return rows.map(([code, name, description]) => ({
    code,
    name,
    description,
    category,
  }));
}

export const STUDIO_PERMISSIONS: readonly PermissionDefinition[] = [
  ...group("clients", [
    ["clients.view", "View Clients", "View clients and their contact details."],
    ["clients.manage", "Manage Clients", "Create, edit and archive clients."],
  ]),
  ...group("projects", [
    [
      "projects.view",
      "View Projects",
      "View projects, rooms and the project workspace.",
    ],
    [
      "projects.manage",
      "Manage Projects",
      "Edit projects, tasks, notes, documents and moodboards.",
    ],
    [
      "proposals.view",
      "View Proposals",
      "View proposals, fees and payment schedules.",
    ],
    [
      "proposals.manage",
      "Manage Proposals",
      "Draft, edit and revise proposals and payment schedules.",
    ],
    [
      "proposals.approve",
      "Approve Proposals",
      "Mark proposals sent, accepted or declined and release instalments for invoicing.",
    ],
  ]),
  ...group("finance", [
    [
      "invoices.view",
      "View Invoices",
      "View invoices, reimbursement and advance requests.",
    ],
    ["invoices.manage", "Prepare Invoices", "Create and edit invoice drafts."],
    [
      "invoices.approve",
      "Approve Invoices",
      "Approve and issue drafts, and put issued invoices on hold.",
    ],
    [
      "invoices.send",
      "Send Invoices",
      "Send issued documents to clients by email or WhatsApp.",
    ],
    [
      "invoices.void",
      "Void Invoices",
      "Void issued documents that were never sent or paid.",
    ],
    [
      "payments.view",
      "View Payments",
      "View payments, allocations and receipts.",
    ],
    [
      "payments.manage",
      "Manage Payments",
      "Record, allocate and refund payments.",
    ],
    [
      "expenses.view",
      "View Business Expenses",
      "View the studio's own operating costs.",
    ],
    [
      "expenses.manage",
      "Manage Business Expenses",
      "Record and edit business expenses.",
    ],
    [
      "owner.view",
      "View Owner Account",
      "View what the studio owes the owner personally.",
    ],
    [
      "owner.manage",
      "Manage Owner Payments",
      "Record owner payments and repayments.",
    ],
  ]),
  ...group("purchasing", [
    [
      "purchases.view",
      "View Purchases",
      "View client purchases, fundings and reimbursements.",
    ],
    [
      "purchases.manage",
      "Manage Purchases",
      "Upload receipts and record client purchases.",
    ],
    ["approvals.view", "View Approvals", "View client approval requests."],
    [
      "approvals.manage",
      "Manage Approvals",
      "Create, send and record client approvals.",
    ],
  ]),
  ...group("banking", [
    [
      "banking.view",
      "View Banking",
      "View bank statements and reconciliation.",
    ],
    ["banking.import", "Import Statements", "Upload bank statement files."],
    [
      "banking.reconcile",
      "Reconcile",
      "Confirm, split and ignore bank matches.",
    ],
  ]),
  ...group("reports", [
    [
      "reports.view",
      "View Reports",
      "View dashboards and profitability reports.",
    ],
    [
      "reports.export",
      "Accountant Export",
      "Export records and documents for the accountant.",
    ],
    ["alerts.view", "View Alerts", "View failed sends, imports and payments."],
    [
      "alerts.manage",
      "Manage Alerts",
      "Resolve or dismiss alerts (owner only).",
    ],
  ]),
  ...group("integrations", [
    [
      "business.settings",
      "Business Settings",
      "Edit business details, bank details and numbering.",
    ],
    [
      "integrations.manage",
      "Manage Integrations",
      "Connect WhatsApp, email and ChatGPT.",
    ],
  ]),
  ...group("portal", [
    [
      "portal.access",
      "Client Portal",
      "Sign in to the client portal (own data only).",
    ],
    [
      "portal.approve",
      "Portal Approvals",
      "Approve or reject items in the client portal.",
    ],
  ]),
];
